import { useMemo, useState, useEffect } from 'react'
import type { ShipmentItem, Shipment } from '../types'
import { apiService } from '../services/apiService'

interface ShipmentChecklistProps {
  shipment: Shipment
  items: ShipmentItem[]
  // CUSMA form props removed - form filling no longer needed
  isStaff?: boolean
  clientId?: string
}

interface ChecklistItem {
  id: string
  label: string
  description: string
  completed: boolean
  required: boolean
}

export default function ShipmentChecklist({ shipment, items, isStaff, clientId }: ShipmentChecklistProps) {
  // Add spinner keyframes
  useEffect(() => {
    if (!document.getElementById('spinner-keyframes')) {
      const style = document.createElement('style')
      style.id = 'spinner-keyframes'
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `
      document.head.appendChild(style)
    }
  }, [])

  const checklistItems = useMemo(() => {
    if (!items.length) return []


    const checklist: ChecklistItem[] = []

    // Check if all items have valid HTS codes
    const allHaveHTS = items.every(item => item.htsCode && item.htsCode.trim() !== '')
    checklist.push({
      id: 'hts_codes',
      label: 'HTS Codes',
      description: `All ${items.length} products have valid HTS codes`,
      completed: allHaveHTS,
      required: true
    })

    // Check if all items have Country of Origin
    const allHaveCOO = items.every(item => item.countryOfOrigin && item.countryOfOrigin.trim() !== '')
    checklist.push({
      id: 'country_of_origin',
      label: 'Country of Origin',
      description: `All ${items.length} products have Country of Origin specified`,
      completed: allHaveCOO,
      required: true
    })

    // Check if all items have COGS (Cost of Goods Sold)
    const allHaveCOGS = items.every(item => 
      item.declaredValue !== undefined && 
      item.declaredValue !== null && 
      item.declaredValue > 0
    )
    checklist.push({
      id: 'cogs',
      label: 'COGS (Cost of Goods)',
      description: `All ${items.length} products have cost values entered`,
      completed: allHaveCOGS,
      required: true
    })

    // Check if all items have manufacturing addresses
    const allHaveManufacturingAddress = items.every(item => 
      item.manufacturingAddress && 
      item.manufacturingAddress.name && 
      item.manufacturingAddress.street_address &&
      item.manufacturingAddress.city &&
      item.manufacturingAddress.country
    )
    checklist.push({
      id: 'manufacturing_address',
      label: 'Manufacturing Address',
      description: `All ${items.length} products have manufacturing addresses`,
      completed: allHaveManufacturingAddress,
      required: true
    })

    // CUSMA form checklist item removed - form filling no longer needed
    // CUSMA checkbox in ShipmentItemRow still works for duty calculations

    return checklist
  }, [items])

  const completedCount = checklistItems.filter(item => item.completed).length
  const totalCount = checklistItems.length
  const allCompleted = completedCount === totalCount


  if (!items.length) {
    return null
  }

  return (
    <div className="card">
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '16px'
      }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>
          Shipment Readiness Checklist
        </h3>
        <div style={{
          padding: '6px 12px',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: 'bold',
          backgroundColor: allCompleted ? '#dcfce7' : '#fee2e2',
          color: allCompleted ? '#166534' : '#991b1b'
        }}>
          {completedCount}/{totalCount} Complete
        </div>
      </div>

      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: '12px'
      }}>
        {checklistItems.map(item => (
          <div
            key={item.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
              backgroundColor: item.completed ? '#f0fdf4' : '#fefefe'
            }}
          >
            {/* Status Icon */}
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: item.completed ? '#10b981' : '#ef4444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px',
              flexShrink: 0
            }}>
              {item.completed ? (
                <svg 
                  width="12" 
                  height="12" 
                  viewBox="0 0 12 12" 
                  fill="none"
                >
                  <path
                    d="M10 3L4.5 8.5L2 6"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg 
                  width="12" 
                  height="12" 
                  viewBox="0 0 12 12" 
                  fill="none"
                >
                  <path
                    d="M9 3L3 9M3 3l6 6"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>

            {/* Content */}
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: item.completed ? '#166534' : '#374151',
                marginBottom: '2px'
              }}>
                {item.label}
                {item.required && (
                  <span style={{ 
                    color: '#dc2626',
                    marginLeft: '4px',
                    fontSize: '14px'
                  }}>
                    *
                  </span>
                )}
              </div>
              <div style={{
                fontSize: '14px',
                color: item.completed ? '#166534' : '#6b7280'
              }}>
                {item.description}
              </div>
            </div>

            {/* Status indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: item.completed ? '#dcfce7' : '#fee2e2',
                color: item.completed ? '#166534' : '#991b1b',
                textTransform: 'uppercase'
              }}>
                {item.completed ? 'Complete' : 'Missing'}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}